import * as items from '@apps-common/js-client/api/items';
import * as searchesApi from '@apps-common/js-client/api/searches';
import * as urls from '@apps-common/js-client/api/urls';
import orderBy from 'lodash/orderBy';
import request, {setAppHeader} from '@apps-common/js-client/api/request';
import {createApi} from '@reduxjs/toolkit/query/react';

import {
  CAPABILITIES_ENDPOINT,
  FOLDERS_API_URL,
  ORDERS_ENDPOINT,
  PDA_ADMIN_URL,
} from '@explorer/config';
import {getAllFolders, moveOrDeleteFolderItems} from './folders.js';
import {normalizeError} from './util.js';

setAppHeader('explorer', [ORDERS_ENDPOINT, PDA_ADMIN_URL]);

// Inspired by: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#axios-basequery
const requestBaseQuery =
  () =>
  ({url, method, data, params}) =>
    request({
      url: url,
      method,
      data,
      params,
    })
      .then(res => ({data: res.data ?? {}}))
      .catch(err => normalizeError(err));

export const apiSlice = createApi({
  baseQuery: requestBaseQuery(),
  tagTypes: ['SavedSearches', 'Folders'],
  endpoints: builder => ({
    getBundleSpec: builder.query({
      query: () => ({
        url: '/explorer-utils/orders-spec',
      }),
    }),
    getBundlesByItemType: builder.query({
      query: () => ({
        url: '/explorer-utils/bundles',
      }),
    }),
    getTilesCapabilities: builder.query({
      query: () => ({
        url: CAPABILITIES_ENDPOINT,
      }),
    }),
    getOrdersPermissions: builder.query({
      query: ({user}) => ({
        url: '/explorer-utils/orders-permissions/',
        params: {
          orgId: user.organization_id,
          programId: user.program_id,
        },
      }),
      transformResponse: resp => {
        //Add default values that we depend on. The API will return an empty object if the user/org are not provisioned
        // in pda-admin
        return {
          'max-assets-per-order': 500,
          'can-clip': false,
          'can-harmonize': false,
          'can-composite': false,
          ...resp,
        };
      },
    }),
    getItemsById: builder.query({
      queryFn: ({itemTypes, itemIds}) => {
        const results = [];
        const onEach = data => {
          results.push(...data);
        };

        return items
          .search({
            types: itemTypes,
            filter: {
              type: 'StringInFilter',
              field_name: 'id',
              config: itemIds,
            },
            each: onEach,
          })
          .then(() => {
            return {data: results};
          });
      },
    }),
    getSavedSearches: builder.query({
      queryFn: () => {
        const searches = [];
        const onEach = async data => {
          const updatedData = data.map(d => {
            const slashIndex = d.name.lastIndexOf('/');
            if (slashIndex > 0) {
              d.folderName = d.name.slice(0, slashIndex);
              d.shortName = d.name.slice(slashIndex + 1);
            }
            return d;
          });

          searches.push(...updatedData);
          // Data API seems to be extra sensitive with 429s
          // this is not the best approach, but until we can dig further
          // it makes a small enough pause between paging requests
          await setTimeout(500);
        };
        return searchesApi
          .search({
            each: onEach,
            retries: 5,
          })
          .then(() => {
            return {
              data: orderBy(searches, 'folderName'),
            };
          })
          .catch(error => normalizeError(error));
      },
      providesTags: result =>
        result?.length
          ? result.map(({id}) => ({type: 'SavedSearches', id}))
          : ['SavedSearches'],
    }),
    deleteSavedSearch: builder.mutation({
      query: searchId => ({
        url: urls.searches(searchId),
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedSearches'],
    }),
    saveOrUpdateSearch: builder.mutation({
      queryFn: options => {
        const promise = options.id
          ? searchesApi.update(options.id, options)
          : searchesApi.create(options);

        return promise
          .then(search => ({data: search}))
          .catch(err => normalizeError(err));
      },
      invalidatesTags: ['SavedSearches'],
    }),
    getFolders: builder.query({
      queryFn: getAllFolders,
      providesTags: result =>
        result?.length
          ? result.map(({id}) => ({type: 'Folders', id}))
          : ['Folders'],
    }),
    getFolder: builder.query({
      query: id => ({
        url: `${FOLDERS_API_URL}/${id}`,
      }),
      providesTags: (result, error, arg) => [{type: 'Folders', id: arg}],
    }),
    updateFolder: builder.mutation({
      query: ({folderId, name, public: isPublic}) => ({
        url: `${FOLDERS_API_URL}/${folderId}`,
        method: 'PATCH',
        data: {
          name,
          public: isPublic,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'Folders', id: arg.folderId},
      ],
    }),
    moveFolderItems: builder.mutation({
      queryFn: ({folderId, toFolder, itemIds}) =>
        moveOrDeleteFolderItems({folderId, toFolder, itemIds, method: 'PUT'}),
      invalidatesTags: (result, error, arg) => {
        return [{type: 'Folders', id: arg.folderId}];
      },
    }),
    deleteFolderItems: builder.mutation({
      queryFn: ({folderId, itemIds}) =>
        moveOrDeleteFolderItems({
          folderId,
          itemIds,
          method: 'DELETE',
        }),
      invalidatesTags: (result, error, arg) => {
        return [{type: 'Folders', id: arg.folderId}];
      },
    }),
    createFolder: builder.mutation({
      query: options => ({
        url: FOLDERS_API_URL,
        method: 'POST',
        data: options,
      }),
      invalidatesTags: ['Folders'],
    }),
    deleteFolder: builder.mutation({
      query: ({folderId}) => ({
        url: `${FOLDERS_API_URL}/${folderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Folders'],
    }),
    getToolLimits: builder.query({
      query: () => ({
        url: '/compute/ops/orders/v2/limits/v0',
      }),
    }),
  }),
});

// Export the auto-generated hook for the query endpoints
export const {
  useGetBundleSpecQuery,
  useGetBundlesByItemTypeQuery,
  useGetTilesCapabilitiesQuery,
  useGetOrdersPermissionsQuery,
  useGetItemsByIdQuery,
  useGetSavedSearchesQuery,
  useDeleteSavedSearchMutation,
  useSaveOrUpdateSearchMutation,
  useGetFoldersQuery,
  useGetFolderQuery,
  useUpdateFolderMutation,
  useMoveFolderItemsMutation,
  useDeleteFolderItemsMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useGetToolLimitsQuery,
} = apiSlice;
